import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { customerGroupLabels, deadlineTone, money, shortDate, stageLabels } from '../lib/format';
import type { CustomerOrder, CustomerOrderGroup, CustomerOverview, ListResponse } from '../types';
import { CustomerHero } from '../components/customer-hero';
import {
  Card,
  EmptyState,
  ErrorNotice,
  LoadingScreen,
  PageHeader,
  StatusBadge,
} from '../components/ui';

const GROUPS: CustomerOrderGroup[] = ['needsAction', 'inProgress', 'waitingArtist', 'completed'];

export function CustomerOrdersPage() {
  const [group, setGroup] = useState<CustomerOrderGroup>('needsAction');
  const overview = useQuery({
    queryKey: ['customer', 'overview'],
    queryFn: () => api<CustomerOverview>('/customer/overview'),
  });
  const orders = useQuery({
    queryKey: ['customer', 'orders', group],
    queryFn: () => api<ListResponse<CustomerOrder>>(`/customer/orders?group=${group}&limit=50`),
  });

  if (overview.isPending) return <LoadingScreen label="Открываем ваши заказы" />;
  if (overview.error) return <ErrorNotice error={overview.error} />;

  const counts = overview.data.counts;
  const total = GROUPS.reduce((sum, key) => sum + counts[key], 0);

  if (total === 0) {
    return (
      <>
        <PageHeader eyebrow="Кабинет заказчика" title="Мои заказы" />
        <EmptyState
          title="Заказов пока нет"
          text="Откройте страницу художника и отправьте заявку — принятая заявка станет заказом."
        />
      </>
    );
  }

  return (
    <>
      <PageHeader eyebrow="Кабинет заказчика" title="Мои заказы" />

      {/*
        Всё, что застряло на заказчике, поднимается наверх и не зависит от
        выбранной вкладки: он открывает кабинет, чтобы ответить, а не искать,
        где именно его ждут.
      */}
      {overview.data.needsAction.slice(0, 2).map((order) => (
        <CustomerHero key={order.id} order={order} />
      ))}

      <div className="chip-row">
        {GROUPS.map((key) => (
          <button
            key={key}
            className={`chip ${key === group ? 'is-active' : ''}`}
            onClick={() => setGroup(key)}
          >
            {customerGroupLabels[key]}
            {counts[key] > 0 && <span className="chip__count">{counts[key]}</span>}
          </button>
        ))}
      </div>

      {orders.isPending && <LoadingScreen label="Загружаем список" />}
      {orders.error && <ErrorNotice error={orders.error} />}
      {orders.data?.items.length === 0 && (
        <EmptyState
          title="Здесь пусто"
          text={`В разделе «${customerGroupLabels[group]}» заказов нет.`}
        />
      )}

      <div className="stack">
        {orders.data?.items.map((order) => (
          <CustomerOrderCard key={order.id} order={order} />
        ))}
      </div>
    </>
  );
}

export function CustomerOrderCard({ order }: { order: CustomerOrder }) {
  return (
    <Link href={`/my/orders/${order.id}`} className="plain-link">
      <Card className="customer-order-card">
        {/* Миниатюра слева: заказ узнаётся по работе, а не по номеру. */}
        <span className="customer-order-card__thumb" aria-hidden="true">
          {order.previewUrl ? (
            <img src={`${order.previewUrl}?size=thumb`} alt="" loading="lazy" />
          ) : (
            order.artist.displayName.slice(0, 1).toUpperCase()
          )}
        </span>

        <div className="customer-order-card__main">
          <div className="card-row">
            <strong>{order.title}</strong>
            <StatusBadge tone={order.group === 'needsAction' ? 'warning' : 'neutral'}>
              {stageLabels[order.currentStage]}
            </StatusBadge>
          </div>
          <p className="meta">
            {order.artist.displayName} ·{' '}
            <span className={`text-${deadlineTone(order.deadlineState)}`}>
              {shortDate(order.deadline)}
            </span>
            {order.queuePosition !== null && order.group !== 'completed'
              ? ` · ${order.queuePosition}-й в очереди`
              : ''}
          </p>

          <div className="payment-progress">
            <span style={{ width: `${order.progress}%` }} />
          </div>

          {order.showPrice && order.payment && (
            <p className="meta">
              {Number(order.payment.balanceDue) <= 0
                ? 'оплачено полностью'
                : `${money(order.payment.paidAmount, order.currency)} из ${money(
                    order.totalPrice!,
                    order.currency,
                  )}`}
            </p>
          )}
        </div>
      </Card>
    </Link>
  );
}
